import { useEffect, useMemo, useState } from "react";
import { Link, generatePath } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  X,
  Lock,
  RotateCcw,
  Trash2,
  Loader2,
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  Save,
  Keyboard,
  Clock,
  ImageOff,
} from "lucide-react";
import { MACHINES_URL } from "@/machines/routes";
import { useSignedImage } from "../../hooks/useSignedImage";

import { Button } from "@/ui/components/ui/button";
import { Switch } from "@/ui/components/ui/switch";
import { Skeleton } from "@/ui/components/ui/skeleton";
import { cn } from "@/ui/lib/utils";
import { formatDateTime, formatNumber } from "@/shared/lib/i18n/formatters.ts";

import BinaryFamilyControl from "./binary-family-control";
import PipelineGroupSection from "./pipeline-group-section";
import LabelTierBadge from "./label-tier-badge";
import {
  useGetReviewDetail,
  useReviewClassification,
  useGetClassificationTaxonomy,
  useGetEventDetail,
} from "../../api";
import { eventStateLabel } from "../../utils/event-colors";
import {
  buildCurrentFamilyState,
  memberToCanonical,
  splitLabelsByTaxonomy,
} from "../../utils/taxonomy";
import {
  buildPipelineGroups,
  computeQualityGate,
  familyForShortcut,
  FAMILY_SHORTCUTS,
  getMemberDisplayOrder,
  groupedShortcutHints,
  nextCycleSelection,
} from "../../utils/pipeline-groups";
import type { ReviewCommand } from "../../types/review";
import type { FamilyMemberSelection } from "../../types/taxonomy";

/** Footer hint shorthands are internal tokens, mapped to display text here. */
const SHORTCUT_GROUP_KEYS: Record<
  string,
  "pipelineGroups.short.core" | "pipelineGroups.short.gate" | "pipelineGroups.short.env"
> = {
  core: "pipelineGroups.short.core",
  gate: "pipelineGroups.short.gate",
  env: "pipelineGroups.short.env",
};

/* ──────────────────────────────────────────────────────────────────
 * ReviewDetailPanel — single-image review sidebar.
 *
 * Keyboard-first design for fast classification review:
 *
 *   B C G     Cycle core families (boulder, material_composition, get)
 *   1 2 3     Cycle gate families (sharpness, occlusion, exposure)
 *   N R D M   Cycle environment (day_night, rain, dust, moisture)
 *   S / Enter Save staged corrections
 *   Q         Reset all staged changes
 *   V         Toggle "mark frame as invalid"
 *   Esc       Close panel
 *
 * Each family control shows its assigned key. Pressing the key cycles
 * members[] → none — "none" unstages (back to the saved label, or
 * removes a staged creation in add mode).
 * ────────────────────────────────────────────────────────────────── */

interface ReviewDetailPanelProps {
  imageId: number;
  onClose: () => void;
  onOpenEvent?: (eventUuid: string) => void;
}

const ReviewDetailPanel = ({
  imageId,
  onClose,
  onOpenEvent,
}: ReviewDetailPanelProps) => {
  const { t } = useTranslation("mlExports");
  const { data: detail, isLoading } = useGetReviewDetail(imageId);
  const { data: taxonomy } = useGetClassificationTaxonomy();
  const reviewMutation = useReviewClassification();
  const { src: imageSrc, onError: handleImageError, isRefreshing, hasFailed } = useSignedImage({
    imageId,
    initialUrl: detail?.signed_url ?? "",
  });

  const { data: eventDetail, isError: eventError } = useGetEventDetail(
    detail?.event_uuid,
  );

  const eventCore = eventDetail?.event;
  const eventLabel = eventCore ? eventStateLabel(eventCore.state, t) : null;
  const eventAvailable = !!eventDetail && !eventError;

  // ─── Jump to the exact event on the machine-detail timeline ──────
  // Rendered as a real link so Ctrl/Cmd/middle-click opens a new tab.
  const machineId = eventCore?.machine_id ?? null;
  const timelineTo =
    machineId != null && detail
      ? `${generatePath(MACHINES_URL.MACHINE_DETAIL, {
          id: String(machineId),
        })}?ts=${new Date(detail.timestamp).getTime()}`
      : null;

  // ─── Local staged state ──────────────────────────────────────────
  const [staged, setStaged] = useState<Partial<Record<string, number>>>({});
  const [dumpValidStaged, setDumpValidStaged] = useState<boolean | null>(null);
  const [familyErrors, setFamilyErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setStaged({});
    setDumpValidStaged(null);
    setFamilyErrors({});
  }, [imageId]);

  const currentState = useMemo(
    () =>
      detail
        ? buildCurrentFamilyState(detail.labels ?? [], taxonomy)
        : new Map(),
    [detail, taxonomy],
  );

  const { otherLabels } = useMemo(
    () => splitLabelsByTaxonomy(detail?.labels ?? [], taxonomy),
    [detail, taxonomy],
  );

  // ─── Pipeline groups + live quality gate ─────────────────────────
  const pipelineGroups = useMemo(() => buildPipelineGroups(taxonomy), [taxonomy]);

  const qualityGate = useMemo(() => {
    const gateGroup = pipelineGroups.find((g) => g.key === "quality_gate");
    if (!gateGroup || gateGroup.families.length === 0) return null;
    // Recomputed live: staged corrections override the saved state.
    return computeQualityGate(gateGroup.families, currentState, staged);
  }, [pipelineGroups, currentState, staged]);

  const stagedCount =
    Object.keys(staged).length + (dumpValidStaged === null ? 0 : 1);

  const effectiveDumpValid =
    dumpValidStaged !== null ? dumpValidStaged : detail?.dump_image_valid ?? true;

  // ─── Handlers ────────────────────────────────────────────────────
  const handleStage = (familyKey: string, selection: FamilyMemberSelection) => {
    setStaged((prev) => {
      const entry = currentState.get(familyKey);
      // "none", re-picking the saved member, or re-clicking a staged
      // creation all unstage — there is nothing to save for this family.
      const isOriginalMember = entry && entry.memberIndex === selection;
      const isClickingStagedMember = !entry && prev[familyKey] === selection;
      if (selection === "none" || isOriginalMember || isClickingStagedMember) {
        const rest = { ...prev };
        delete rest[familyKey];
        return rest;
      }
      return { ...prev, [familyKey]: selection };
    });
  };

  /** Cycle a family: members[] → none → members[0]. */
  const handleCycle = (familyKey: string) => {
    const family = taxonomy?.families.find((f) => f.key === familyKey);
    if (!family || family.members.length === 0) return;
    const entry = currentState.get(familyKey);
    // Corrected rows are locked — undo the correction first.
    if (entry?.label.review_state === "MODEL_CORRECTED") return;
    const current = entry?.memberIndex ?? null;
    const effective = staged[familyKey] ?? current ?? "none";
    let next = nextCycleSelection(family.members.length, effective);
    // "none" on an unstaged row ≡ the saved label — skip it so the
    // cycle keeps moving when the saved member is the last one.
    if (next === "none" && current !== null && staged[familyKey] === undefined) {
      next = nextCycleSelection(family.members.length, next);
    }
    handleStage(familyKey, next);
  };

  const handleUndoCorrection = (familyKey: string) => {
    const entry = currentState.get(familyKey);
    if (!entry) return;
    const isHumanAdded = entry.label.origin === "human";
    const isModelCorrected =
      entry.label.origin === "model" && entry.label.human_label != null;
    if (!isHumanAdded && !isModelCorrected) return;

    const cmd: ReviewCommand = isHumanAdded
      ? { type: "delete", id: entry.label.id }
      : { type: "update", id: entry.label.id, human_label: null };

    reviewMutation.mutate(
      { imageId, payload: { commands: [cmd] } },
      {
        onSuccess: () =>
          toast.success(
            isHumanAdded
              ? t("toast.labelRemoved")
              : t("toast.correctionUndone"),
          ),
        onError: () => toast.error(t("toast.undoFailed")),
      },
    );
  };

  const handleReset = () => {
    setStaged({});
    setDumpValidStaged(null);
  };

  const handleToggleValid = () =>
    setDumpValidStaged((prev) =>
      prev === null ? !(detail?.dump_image_valid ?? true) : !prev,
    );

  const handleSave = async () => {
    if (!taxonomy || stagedCount === 0) return;
    const commands: ReviewCommand[] = [];
    for (const [familyKey, memberIndex] of Object.entries(staged)) {
      if (memberIndex === undefined) continue;
      const family = taxonomy.families.find((f) => f.key === familyKey);
      if (!family) continue;
      const canonical = memberToCanonical(family, memberIndex);
      if (!canonical) continue;
      const entry = currentState.get(familyKey);
      if (!entry) {
        // Add mode — no model prediction, create a fresh human label.
        commands.push({ type: "create", human_label: canonical });
      } else if (entry.label.origin === "human") {
        // Changing a human-added label = delete + create (update would
        // fail — origin="human" rows are not corrections).
        commands.push({ type: "delete", id: entry.label.id });
        commands.push({ type: "create", human_label: canonical });
      } else {
        commands.push({
          type: "update",
          id: entry.label.id,
          human_label: canonical,
        });
      }
    }
    const payload = {
      commands,
      ...(dumpValidStaged !== null
        ? { dump_image_valid: dumpValidStaged }
        : {}),
    };

    try {
      await reviewMutation.mutateAsync({ imageId, payload });
      toast.success(
        commands.length > 0
          ? t("toast.saved", {
              count: commands.length,
              value: formatNumber(commands.length),
            })
          : t("toast.savedPlain"),
      );
      setStaged({});
      setDumpValidStaged(null);
    } catch (err: unknown) {
      const apiError = (err as {
        response?: { data?: { error?: {
          detail?: string;
          code?: string;
          reason?: string;
          image_id?: number;
          label_id?: number;
        } } }
      })?.response?.data?.error;

      if (apiError?.code === "command_error" && apiError.label_id && taxonomy) {
        const errorFamily = taxonomy.families.find(
          (f) => {
            const entry = currentState.get(f.key);
            return entry?.label.id === apiError.label_id;
          }
        );
        if (errorFamily) {
          setFamilyErrors({
            [errorFamily.key]:
              apiError.detail ??
              t("toast.correctionRejected", {
                reason: apiError.reason ?? t("toast.unknownReason"),
              }),
          });
        } else {
          toast.error(apiError.detail ?? t("toast.saveFailed"));
        }
      } else {
        toast.error(apiError?.detail ?? t("toast.saveFailed"));
      }
    }
  };

  // ─── Keyboard shortcuts ──────────────────────────────────────────
  useEffect(() => {
    if (!taxonomy) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      )
        return;

      const key = e.key.toLowerCase();

      // Pipeline shortcuts: B C G core · 1 2 3 gate · N R D M env
      const cycled = familyForShortcut(taxonomy, key);
      if (cycled) {
        e.preventDefault();
        handleCycle(cycled.key);
        return;
      }

      switch (key) {
        case "escape":
          e.preventDefault();
          onClose();
          break;
        case "s":
        case "enter":
          e.preventDefault();
          handleSave();
          break;
        case "q":
          e.preventDefault();
          handleReset();
          break;
        case "v":
          e.preventDefault();
          handleToggleValid();
          break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxonomy, currentState, staged, stagedCount, dumpValidStaged, detail]);

  // ─── Helpers ──────────────────────────────────────────────────────

  const otherCategoryLabel = (category: string): string => {
    if (category === "legacy") return t("otherLabels.legacy");
    if (category === "invalid") return t("otherLabels.invalid");
    return t("otherLabels.unknown");
  };

  return (
    <div className="w-[440px] border-l bg-background flex flex-col h-full">
      {/* ─── Header ─────────────────────────────────────────────── */}
      <div className="flex flex-col px-4 py-3 border-b gap-2 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-sm">
              {detail
                ? t("detail.header.titleWithId", { id: detail.image_id })
                : t("detail.header.title")}
            </h2>
            {detail?.total_frames != null && (
              <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {t("detail.header.framePosition", {
                  frame: detail.event_frame,
                  total: detail.total_frames,
                })}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-accent transition-colors"
            title={t("detail.header.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Event context */}
        {detail?.event_uuid && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {eventCore && eventLabel ? (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wide text-white"
                  style={{ backgroundColor: eventCore.color }}
                >
                  {eventLabel}
                </span>
              ) : eventError ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wide bg-muted text-muted-foreground">
                  {t("eventStates.fallback")}
                </span>
              ) : (
                <Skeleton className="h-4 w-12" />
              )}
              <span className="text-[10px] text-muted-foreground font-mono truncate">
                {detail.event_uuid.slice(0, 8)}
              </span>
            </div>

            {onOpenEvent && (
              <button
                onClick={() =>
                  eventAvailable && onOpenEvent(detail.event_uuid)
                }
                disabled={!eventAvailable}
                className={cn(
                  "inline-flex items-center gap-1 text-xs text-primary hover:underline shrink-0 transition-opacity",
                  !eventAvailable &&
                    "opacity-40 cursor-not-allowed hover:no-underline",
                )}
                title={
                  eventError
                    ? t("detail.header.eventUnavailable")
                    : !eventDetail
                      ? t("detail.header.eventLoading")
                      : t("detail.header.openEventPanel")
                }
              >
                {t("detail.header.viewEvent")} <ArrowRight className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {/* Jump to this exact event on the machine-detail timeline.
            Ctrl/Cmd/middle-click opens it in a new tab. */}
        {timelineTo && (
          <Link
            to={timelineTo}
            className="inline-flex items-center gap-1.5 self-start text-xs text-primary hover:underline transition-colors"
            title={t("detail.header.timelineTitle")}
          >
            <ExternalLink className="h-3 w-3" />
            {t("detail.header.openInTimeline")}
          </Link>
        )}
      </div>

      {/* ─── Body ───────────────────────────────────────────────── */}
      {isLoading || !detail ? (
        <div className="p-4 space-y-4">
          <Skeleton className="aspect-square w-full rounded-lg" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* Image */}
          <div className="p-4 pb-2">
            <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
              {hasFailed ? (
                <div className="w-full h-full flex items-center justify-center bg-muted rounded-lg">
                  <ImageOff className="h-12 w-12 text-muted-foreground" />
                </div>
              ) : (
                <img
                  src={imageSrc}
                  alt={t("detail.imageAlt", { id: imageId })}
                  className={cn(
                    "w-full h-full object-contain transition-opacity duration-200",
                    isRefreshing && "opacity-50",
                  )}
                  onError={handleImageError}
                />
              )}
              {/* Invalid overlay */}
              {!effectiveDumpValid && (
                <div className="absolute inset-0 bg-yellow-900/20 border-2 border-dashed border-yellow-500/50 rounded-lg flex items-center justify-center">
                  <span className="px-2 py-1 rounded bg-yellow-600/90 text-white text-[10px] font-bold uppercase tracking-wider">
                    {t("detail.markedInvalid")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Metadata row */}
          <div className="px-4 pb-3 flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
            <span className="font-medium text-foreground">
              {detail.machine_name}
            </span>
            <span>·</span>
            <span>{t("detail.meta.frame", { frame: detail.event_frame })}</span>
            <span>·</span>
            <span>{formatDateTime(new Date(detail.timestamp))}</span>
          </div>

          {/* Frame validity */}
          <div className="mx-4 mb-4 rounded-lg border p-3 flex items-center justify-between bg-muted/20">
            <div className="flex items-center gap-2.5">
              <AlertTriangle
                className={cn(
                  "h-4 w-4 shrink-0",
                  !effectiveDumpValid
                    ? "text-yellow-500"
                    : "text-muted-foreground/50",
                )}
              />
              <div>
                <div className="text-xs font-medium">
                  {t("validity.title")}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  <Trans
                    t={t}
                    i18nKey="validity.hint"
                    components={{
                      key: (
                        <kbd className="px-1 py-0.5 rounded bg-muted border text-[9px] font-mono" />
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
            <Switch
              checked={!effectiveDumpValid}
              onCheckedChange={handleToggleValid}
            />
          </div>

          {/* ─── Binary families ────────────────────────────────── */}
          <div className="px-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                {t("classification.title")}
              </h3>
              <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <Keyboard className="h-3 w-3" />
                {t("classification.keysHint")}
              </span>
            </div>

            {!taxonomy ? (
              <div className="space-y-2">
                <Skeleton className="h-14 w-full rounded-lg" />
                <Skeleton className="h-14 w-full rounded-lg" />
              </div>
            ) : (
              <div className="space-y-4">
                {pipelineGroups.map((group) => (
                  <PipelineGroupSection
                    key={group.key}
                    group={group}
                    gate={group.key === "quality_gate" ? qualityGate : undefined}
                  >
                    {group.families.map((family) => {
                      const entry = currentState.get(family.key);
                      const currentIndex = entry?.memberIndex ?? null;
                      const reviewState = entry?.label.review_state;
                      const stagedIndex = staged[family.key];

                      const selectedIndex: FamilyMemberSelection =
                        stagedIndex !== undefined
                          ? stagedIndex
                          : currentIndex ?? "none";

                      const isCorrected = reviewState === "MODEL_CORRECTED";
                      const isHumanAdded = reviewState === "HUMAN_ADDED";
                      const isOutdated = reviewState === "REVIEW_OUTDATED";
                      const isStaged = stagedIndex !== undefined;

                      const conf =
                        entry?.label.confidence != null
                          ? t("caption.confidence", {
                              value: formatNumber(
                                Math.round(entry.label.confidence * 100),
                              ),
                            })
                          : "";

                      const captionParts: React.ReactNode[] = [];
                      if (entry) {
                        captionParts.push(
                          <span key="origin">
                            {isHumanAdded
                              ? t("caption.addedManually")
                              : isOutdated
                                ? t("caption.outdated", { conf })
                                : isCorrected
                                  ? t("caption.corrected", { conf })
                                  : t("caption.model", { conf })}
                          </span>,
                        );
                        captionParts.push(
                          <LabelTierBadge
                            key="tier"
                            label={entry.label}
                            alert={detail?.alert}
                            className="ml-1.5 align-middle"
                          />,
                        );
                        if (isStaged) {
                          captionParts.push(
                            <span
                              key="arrow"
                              className="text-amber-600 dark:text-amber-400 font-medium"
                            >
                              {t("caption.staged")}
                            </span>,
                          );
                        }
                      } else {
                        captionParts.push(
                          <span key="none" className="italic">
                            {isStaged ? (
                              <span className="text-violet-600 dark:text-violet-400 font-medium not-italic">
                                {t("caption.willAdd")}
                              </span>
                            ) : (
                              t("caption.noPrediction")
                            )}
                          </span>,
                        );
                      }

                      return (
                        <div key={family.key}>
                          <BinaryFamilyControl
                            family={family}
                            currentIndex={currentIndex}
                            selectedIndex={selectedIndex}
                            disabled={isCorrected}
                            staged={isStaged}
                            corrected={isCorrected}
                            reviewOutdated={isOutdated}
                            humanAdded={isHumanAdded}
                            displayOrder={getMemberDisplayOrder(family)}
                            gateDot={
                              group.key === "quality_gate"
                                ? qualityGate?.families[family.key]
                                : undefined
                            }
                            shortcutKey={FAMILY_SHORTCUTS[family.key]}
                            caption={<>{captionParts}</>}
                            onChange={(selection) => {
                              setFamilyErrors((prev) => {
                                const rest = { ...prev };
                                delete rest[family.key];
                                return rest;
                              });
                              handleStage(family.key, selection);
                            }}
                            errorMessage={familyErrors[family.key]}
                          />
                          {entry && (isHumanAdded || entry.label.human_label != null) && (
                            <button
                              onClick={() => handleUndoCorrection(family.key)}
                              className="mt-1 text-[11px] text-primary hover:underline inline-flex items-center gap-1 transition-colors"
                            >
                              {isHumanAdded ? (
                                <Trash2 className="h-3 w-3" />
                              ) : (
                                <RotateCcw className="h-3 w-3" />
                              )}
                              {isHumanAdded
                                ? t("detail.removeLabel")
                                : t("detail.undoCorrection")}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </PipelineGroupSection>
                ))}
              </div>
            )}
          </div>

          {/* ─── Other labels (legacy, invalid, unknown) ────────── */}
          {otherLabels.length > 0 && taxonomy && (
            <div className="px-4 pb-4">
              <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
                {t("otherLabels.title")}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {otherLabels.map((label) => {
                  const meta = taxonomy.other_labels.find(
                    (o) => o.canonical === label.final_label,
                  );
                  const isLegacy = meta?.category === "legacy";
                  return (
                    <div
                      key={label.id}
                      className={cn(
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border",
                        isLegacy
                          ? "border-dashed border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400"
                          : "bg-muted text-muted-foreground",
                      )}
                      title={
                        meta ? otherCategoryLabel(meta.category) : undefined
                      }
                    >
                      {isLegacy ? (
                        <Clock className="h-2.5 w-2.5" />
                      ) : (
                        <Lock className="h-2.5 w-2.5" />
                      )}
                      {meta?.display_name ?? label.final_label}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Footer — save bar ──────────────────────────────────── */}
      <div className="border-t px-4 py-3 shrink-0">
        {stagedCount > 0 ? (
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
              {t("detail.footer.stagedCount", {
                count: stagedCount,
                value: formatNumber(stagedCount),
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                className="text-xs h-8"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                {t("detail.footer.reset")}
                <kbd className="ml-1.5 text-[9px] opacity-60">Q</kbd>
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={reviewMutation.isPending}
                className="h-8"
              >
                {reviewMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                )}
                {t("detail.footer.save")}
                <kbd className="ml-1.5 text-[9px] opacity-60">S</kbd>
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-[10px] text-muted-foreground">
            {taxonomy && (
              <div className="flex flex-wrap justify-center items-center gap-x-2 gap-y-0.5">
                {groupedShortcutHints().map((group, i) => (
                  <span
                    key={group.label}
                    className="inline-flex items-center gap-1"
                  >
                    {i > 0 && <span className="opacity-40">·</span>}
                    {group.keys.map((key) => (
                      <kbd
                        key={key}
                        className="inline-flex items-center justify-center h-4 min-w-4 px-0.5 rounded bg-muted border text-[9px] font-mono font-semibold uppercase"
                      >
                        {key}
                      </kbd>
                    ))}
                    <span className="opacity-70">
                      {t(SHORTCUT_GROUP_KEYS[group.label] ?? "pipelineGroups.short.core")}
                    </span>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1 py-0.5 rounded bg-muted border text-[9px] font-mono">
                  S
                </kbd>
                {t("detail.footer.saveHint")}
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1 py-0.5 rounded bg-muted border text-[9px] font-mono">
                  V
                </kbd>
                {t("detail.footer.invalidHint")}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewDetailPanel;
